import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Tab,
  Tabs,
  Button,
  Badge,
  Card,
  CardContent,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Check as CheckIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  NotificationsActive as NotificationsActiveIcon,
  NotificationsOff as NotificationsOffIcon,
  Info as InfoIcon,
  Assessment as AssessmentIcon,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- reserved for view-details UI
  Visibility as VisibilityIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';
import { useNavigate } from 'react-router-dom';
import formatDistanceToNow from 'date-fns/formatDistanceToNow';
import { format } from 'date-fns';

interface PlatformNotification {
  notification_id: string;
  title: string;
  message: string;
  type: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  is_read: boolean;
  action_url: string;
  created_at: string;
}

const Notifications: React.FC = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<PlatformNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [markingAsRead, setMarkingAsRead] = useState<{[key: string]: boolean}>({});
  const [aiAnalysisDialog, setAiAnalysisDialog] = useState<{
    open: boolean;
    safetyCheckId: string | null;
    loading: boolean;
    data: any;
    error: string | null;
  }>({
    open: false,
    safetyCheckId: null,
    loading: false,
    data: null,
    error: null,
  });

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/notifications/platform?excludeTypes=incident,incident_update`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      setNotifications(response.data.data || []);
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setError('Failed to load notifications. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const handleMarkAsRead = async (notificationId: string) => {
    try {
      setMarkingAsRead(prev => ({ ...prev, [notificationId]: true }));
      const token = localStorage.getItem('token');
      
      await axios.put(
        `${API_BASE_URL}/notifications/platform/${notificationId}/read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state
      setNotifications(prev => 
        prev.map(n => 
          n.notification_id === notificationId 
            ? { ...n, is_read: true } 
            : n
        )
      );
    } catch (err) {
      console.error('Error marking notification as read:', err);
      setError('Failed to mark notification as read.');
    } finally {
      setMarkingAsRead(prev => ({ ...prev, [notificationId]: false }));
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const markPromises = notifications
        .filter(n => !n.is_read)
        .map(n => 
          axios.put(
            `${API_BASE_URL}/notifications/platform/${n.notification_id}/read`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          )
        );
      
      await Promise.all(markPromises);
      
      // Update all notifications in state as read
      setNotifications(prev => 
        prev.map(n => ({ ...n, is_read: true }))
      );
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
      setError('Failed to mark all notifications as read.');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const getSeverityIcon = (severity: string, type: string) => {
    if (type === 'incident') {
      return <ErrorIcon color="error" fontSize="small" />;
    } else if (type === 'safety_check') {
      return <WarningIcon color="warning" fontSize="small" />;
    } else {
      switch (severity) {
        case 'critical':
        case 'high':
          return <ErrorIcon color="error" fontSize="small" />;
        case 'medium':
          return <WarningIcon color="warning" fontSize="small" />;
        case 'low':
        default:
          return <InfoIcon color="info" fontSize="small" />;
      }
    }
  };

  const getSeverityColor = (severity: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (severity) {
      case 'critical':
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'info';
      default:
        return 'default';
    }
  };

  const extractSafetyCheckId = (actionUrl: string, title: string): string | null => {
    if (!actionUrl) return null;
    const patterns = [
      /\/safety-checks\/([^/?]+)/,
      /\/admin\/safety-checks\/analysis\/([^/?]+)/,
      /safety_check_id=([^&]+)/,
      /safetyCheckId=([^&]+)/,
      /check_id=([^&]+)/,
    ];
    for (const pattern of patterns) {
      const match = actionUrl.match(pattern);
      if (match) return match[1];
    }
    return null;
  };

  const filteredNotifications = tabValue === 0 
    ? notifications
    : tabValue === 1 
      ? notifications.filter(n => !n.is_read)
      : notifications.filter(n => n.is_read);

  const highPriorityCount = notifications.filter(n => !n.is_read && (n.severity === 'high' || n.severity === 'critical')).length;
  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Badge 
            badgeContent={highPriorityCount} 
            color="error"
            sx={{ mr: 2 }}
          >
            <NotificationsActiveIcon fontSize="large" color="primary" />
          </Badge>
          <Typography variant="h4">
            Notifications
          </Typography>
        </Box>
        
        {unreadCount > 0 && (
          <Button 
            variant="outlined" 
            onClick={handleMarkAllAsRead}
            startIcon={<CheckIcon />}
            disabled={loading}
          >
            Mark All as Read
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h6" gutterBottom>High Priority</Typography>
              <Typography variant="h3" color="error.main">{highPriorityCount}</Typography>
              <Typography variant="body2" color="text.secondary">Critical issues requiring immediate attention</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h6" gutterBottom>Unread</Typography>
              <Typography variant="h3" color="primary.main">{unreadCount}</Typography>
              <Typography variant="body2" color="text.secondary">Notifications awaiting your review</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h6" gutterBottom>Total</Typography>
              <Typography variant="h3">{notifications.length}</Typography>
              <Typography variant="body2" color="text.secondary">Total notifications in your system</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab 
            label="All" 
            icon={<Badge badgeContent={notifications.length} color="default" />} 
            iconPosition="end"
          />
          <Tab 
            label="Unread" 
            icon={<Badge badgeContent={unreadCount} color="primary" />} 
            iconPosition="end"
          />
          <Tab 
            label="Read" 
            icon={<Badge badgeContent={notifications.filter(n => n.is_read).length} color="default" />} 
            iconPosition="end"
          />
        </Tabs>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress />
        </Box>
      ) : filteredNotifications.length === 0 ? (
        <Box 
          sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center', 
            p: 5, 
            bgcolor: 'background.paper',
            borderRadius: 1
          }}
        >
          <NotificationsOffIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No notifications to display
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {tabValue === 1 
              ? "You've read all your notifications." 
              : tabValue === 2 
                ? "You don't have any read notifications yet." 
                : "You don't have any notifications yet."}
          </Typography>
        </Box>
      ) : (
        <Paper>
          <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
            {filteredNotifications.map((notification, index) => (
              <React.Fragment key={notification.notification_id}>
                <ListItem 
                  alignItems="flex-start"
                  sx={{ 
                    transition: 'background-color 0.2s',
                    bgcolor: !notification.is_read ? 'action.hover' : 'inherit',
                    '&:hover': { bgcolor: 'action.selected' }
                  }}
                  secondaryAction={
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                      {notification.type === 'safety_check' && (
                        <Tooltip title="View AI Report">
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={async () => {
                              let checkId = extractSafetyCheckId(notification.action_url, notification.title);
                              
                              // If we don't have the ID, try to find it by querying failed checks
                              if (!checkId) {
                                try {
                                  const token = localStorage.getItem('token');
                                  const checkNameMatch = notification.title.match(/Failed Safety Check: (.+)/);
                                  
                                  if (checkNameMatch) {
                                    const checkName = checkNameMatch[1].trim();
                                    
                                    try {
                                      const responsesRes = await axios.get(
                                        `${API_BASE_URL}/check-responses?check_status=FAIL,fail&limit=20`,
                                        { headers: { Authorization: `Bearer ${token}` } }
                                      );
                                      
                                      if (responsesRes.data?.success && responsesRes.data.data?.length > 0) {
                                        const failedChecks = responsesRes.data.data;
                                        const matchingCheck = failedChecks.find((check: any) => {
                                          const checkNameFromData = check.check_name || '';
                                          return checkNameFromData === checkName || 
                                                 checkNameFromData.includes(checkName) ||
                                                 checkName.includes(checkNameFromData);
                                        });
                                        
                                        if (matchingCheck?.safety_check_id) {
                                          checkId = matchingCheck.safety_check_id;
                                        } else if (failedChecks.length > 0) {
                                          const sortedChecks = failedChecks.sort((a: any, b: any) => {
                                            const dateA = new Date(a.created_at || 0).getTime();
                                            const dateB = new Date(b.created_at || 0).getTime();
                                            return dateB - dateA;
                                          });
                                          checkId = sortedChecks[0]?.safety_check_id;
                                        }
                                      }
                                    } catch (err) {
                                      console.error('Error querying check responses:', err);
                                    }
                                  }
                                } catch (err) {
                                  console.error('Error finding safety check ID:', err);
                                }
                              }
                              
                              if (!checkId) {
                                alert('Unable to find the safety check. Please navigate to Safety Checks to view the AI analysis.');
                                return;
                              }
                              
                              setAiAnalysisDialog({
                                open: true,
                                safetyCheckId: checkId,
                                loading: true,
                                data: null,
                                error: null,
                              });
                              try {
                                const token = localStorage.getItem('token');
                                const res = await axios.get(
                                  `${API_BASE_URL}/check-responses/analysis/${checkId}`,
                                  { headers: { Authorization: `Bearer ${token}` } }
                                );
                                if (res.data?.success && res.data.data && res.data.data.length > 0) {
                                  const latestAnalysis = res.data.data[0];
                                  const analysisText = latestAnalysis.analysis_text || '';
                                  const isFail = analysisText.trim().split('\n')[0] === 'FAIL';
                                  setAiAnalysisDialog(prev => ({
                                    ...prev,
                                    loading: false,
                                    data: {
                                      analysis_text: analysisText,
                                      detailed_explanation: analysisText,
                                      should_mark_out_of_action: isFail,
                                      created_at: latestAnalysis.created_at,
                                      analysis_id: latestAnalysis.analysis_id
                                    },
                                    error: null,
                                  }));
                                } else {
                                  setAiAnalysisDialog(prev => ({
                                    ...prev,
                                    loading: false,
                                    error: 'No AI analysis found for this safety check',
                                  }));
                                }
                              } catch (e: any) {
                                setAiAnalysisDialog(prev => ({
                                  ...prev,
                                  loading: false,
                                  error: e.response?.data?.message || 'Failed to fetch AI analysis',
                                }));
                              }
                            }}
                            sx={{ 
                              color: 'text.secondary',
                              '&:hover': { 
                                color: 'primary.main',
                                bgcolor: 'action.hover'
                              }
                            }}
                          >
                            <AssessmentIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {!notification.is_read && (
                      <Tooltip title="Mark as Read">
                        <IconButton
                          edge="end"
                          onClick={() => handleMarkAsRead(notification.notification_id)}
                          disabled={!!markingAsRead[notification.notification_id]}
                            sx={{ 
                              color: 'success.main',
                              '&:hover': { 
                                bgcolor: 'success.50'
                              }
                            }}
                        >
                          {markingAsRead[notification.notification_id] ? (
                            <CircularProgress size={20} />
                          ) : (
                            <CheckIcon />
                          )}
                        </IconButton>
                      </Tooltip>
                      )}
                    </Box>
                  }
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box 
                          onClick={() => notification.action_url && navigate(notification.action_url)}
                          sx={{ 
                            cursor: notification.action_url ? 'pointer' : 'default',
                            '&:hover': notification.action_url ? { textDecoration: 'underline' } : {},
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                          }}
                        >
                          {getSeverityIcon(notification.severity, notification.type)}
                          <Typography variant="subtitle1" fontWeight={!notification.is_read ? 'bold' : 'normal'}>
                            {notification.title}
                          </Typography>
                        </Box>
                        <Chip 
                          size="small" 
                          label={notification.severity} 
                          color={getSeverityColor(notification.severity)}
                          sx={{ ml: 1 }}
                        />
                        <Chip 
                          size="small" 
                          label={notification.type} 
                          variant="outlined"
                        />
                      </Box>
                    }
                    secondary={
                      <React.Fragment>
                        <Typography component="span" variant="body2" sx={{ 
                          display: 'block', 
                          mt: 1,
                          color: !notification.is_read ? 'text.primary' : 'text.secondary',
                          fontWeight: !notification.is_read ? 'medium' : 'normal'
                        }}>
                          {notification.message}
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            {format(new Date(notification.created_at), 'PPp')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                          </Typography>
                        </Box>
                      </React.Fragment>
                    }
                  />
                </ListItem>
                {index < filteredNotifications.length - 1 && <Divider component="li" />}
              </React.Fragment>
            ))}
          </List>
        </Paper>
      )}

      {/* AI Analysis Dialog */}
      <Dialog
        open={aiAnalysisDialog.open}
        onClose={() => setAiAnalysisDialog({ open: false, safetyCheckId: null, loading: false, data: null, error: null })}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <DialogTitle sx={{ flexShrink: 0, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              AI Analysis
            </Typography>
            <IconButton
              aria-label="close"
              onClick={() => setAiAnalysisDialog({ open: false, safetyCheckId: null, loading: false, data: null, error: null })}
              sx={{ color: (theme) => theme.palette.grey[500] }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent 
          dividers
          sx={{
            flex: '1 1 auto',
            overflowY: 'auto',
            minHeight: 0,
            py: 3
          }}
        >
          {aiAnalysisDialog.loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
              <CircularProgress />
            </Box>
          ) : aiAnalysisDialog.error ? (
            <Alert severity="error">{aiAnalysisDialog.error}</Alert>
          ) : aiAnalysisDialog.data ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Status Summary */}
              <Box sx={{ 
                p: 2.5, 
                bgcolor: aiAnalysisDialog.data.should_mark_out_of_action ? '#fff5f5' : '#f1f8f4', 
                borderRadius: 2,
                border: '2px solid',
                borderColor: aiAnalysisDialog.data.should_mark_out_of_action ? 'error.main' : 'success.main'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5, flexWrap: 'wrap' }}>
                  <Chip
                    label={aiAnalysisDialog.data.should_mark_out_of_action ? 'FAIL' : 'PASS'}
                    color={aiAnalysisDialog.data.should_mark_out_of_action ? 'error' : 'success'}
                    sx={{ fontWeight: 'bold', fontSize: '0.875rem', height: '32px' }}
                  />
                </Box>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  Analysis Date: {new Date(aiAnalysisDialog.data.created_at).toLocaleString()}
                </Typography>
              </Box>

              {/* Detailed Explanation */}
              {(aiAnalysisDialog.data.detailed_explanation || aiAnalysisDialog.data.analysis_text) && (
                <Box sx={{ 
                  p: 2.5, 
                  borderRadius: 2,
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider'
                }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: 'text.primary' }}>
                    Detailed Analysis
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      lineHeight: 1.8,
                      color: 'text.primary'
                    }}
                  >
                    {aiAnalysisDialog.data.detailed_explanation || aiAnalysisDialog.data.analysis_text}
                  </Typography>
                </Box>
              )}

              {!aiAnalysisDialog.data.detailed_explanation && !aiAnalysisDialog.data.analysis_text && (
                <Alert severity="info">
                  No detailed analysis available for this safety check.
                </Alert>
              )}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
          <Button
            onClick={() => setAiAnalysisDialog({ open: false, safetyCheckId: null, loading: false, data: null, error: null })}
            variant="outlined"
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Notifications; 